<template>
    <div class="game-screen">
        <GameHeader
            :score="score"
            :time-left="timeLeft"
        />

        <ShelfContainer
            :placed-products="placedProducts"
            :correct-shelf="currentQuestion.correctShelf"
            :dragged-product="draggedProduct"
            @drop="handleDrop"
            @drag-enter="handleDragEnter"
            @drag-leave="handleDragLeave"
        />

        <QuestionBox :question="currentQuestion.question" />

        <AnswerProducts
            :products="products"
            :dragged-product="draggedProduct"
            @drag-start="handleDragStart"
            @drag-end="handleDragEnd"
            @touch-move="handleTouchMove"
            @touch-drop="handleTouchDrop"
        />

        <Feedback :feedback="feedback" />
    </div>
</template>

<script setup lang="ts">
    import type { Product, CurrentQuestion, PlacedProduct, FeedbackMessage } from '../../types/quiz';
    import GameHeader from './GameHeader.vue';
    import ShelfContainer from './ShelfContainer.vue';
    import QuestionBox from './QuestionBox.vue';
    import AnswerProducts from './AnswerProducts.vue';
    import Feedback from './Feedback.vue';

    defineProps<{
        score: number;
        timeLeft: number;
        currentQuestion: CurrentQuestion;
        draggedProduct: string | null;
        feedback: FeedbackMessage | null;
        placedProducts: Record<number, PlacedProduct[]>;
        products: Product[];
    }>();

    const emit = defineEmits<{
        drop: [shelfNum: number, event: DragEvent];
        dragStart: [productId: string, event: DragEvent | TouchEvent];
        dragEnd: [];
        dragEnter: [shelfNum: number];
        dragLeave: [];
        touchMove: [event: TouchEvent];
        touchDrop: [event: TouchEvent];
    }>();

    const handleDrop = (shelfNum: number, event: DragEvent) => {
        emit('drop', shelfNum, event);
    };

    const handleDragStart = (productId: string, event: DragEvent | TouchEvent) => {
        emit('dragStart', productId, event);
    };

    const handleDragEnd = () => {
        emit('dragEnd');
    };

    const handleDragEnter = (shelfNum: number) => {
        emit('dragEnter', shelfNum);
    };

    const handleDragLeave = () => {
        emit('dragLeave');
    };

    const handleTouchMove = (event: TouchEvent) => {
        emit('touchMove', event);
    };

    const handleTouchDrop = (event: TouchEvent) => {
        emit('touchDrop', event);
    };
</script>

<style scoped>
    .game-screen {
        position: relative;
        height: 100%;
        width: 100%;
    }
</style>
